<template>
    <div class="record-comments">
        <div class="comment-list">
            <div v-for="comment in comments" :key="comment._id" :class="['comment-item', {'private-comment': $_.get(comment, 'visibility.mode') === 'private'}]">
                <div class="comment-header">
                    <Avatar 
                        :size="35"
                        :src="getUserAvatar(comment.user)"
                        :username="getUserName(comment.user)"
                    />
                    <span class="comment-username">{{ getUserName(comment.user) }}</span>
                    <span v-if="$_.get(comment, 'visibility.mode') === 'private'" class="private-badge">
                        <a-icon type="lock" /> {{ $t("chatWindow.private") }}
                    </span>
                    <span class="comment-date">{{ formatDate(comment.createdAt) }}</span>
                </div>
                <div class="comment-body">
                    <div v-if="editingCommentId === comment._id">
                        <textarea v-model="editedCommentMessage"></textarea>
                        <button @click="cancelEdit">{{ $t("tables.cancel") }}</button>
                        <button @click="saveComment(comment._id)">{{ $t("tables.save") }}</button>
                    </div>
                    <div v-else>
                        {{ comment.message }}
                    </div>
                </div>
                <div v-if="isCommentOwner(comment)" class="comment-actions">
                    <a @click="editComment(comment)">{{ $t("message.edit") }}</a>
                    <a @click="deleteComment(comment._id)">{{ $t("message.delete") }}</a>
                </div>
            </div>
        </div>

        <div class="new-comment">
            <textarea v-model="newCommentMessage" :placeholder="$t('task.addComment')"></textarea>
            <div class="comment-controls">
                <a-radio-group v-model="commentVisibilityMode" class="visibility-toggle">
                    <a-radio value="public">
                        <a-icon type="global" /> {{ $t("message.public") }}
                    </a-radio>
                    <a-radio value="private">
                        <a-icon type="lock" /> {{ $t("chatWindow.private") }}
                    </a-radio>
                </a-radio-group>
                <button @click="addComment">{{ $t("task.addComment") }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import Avatar from "vue-avatar";
import moment from "moment";
import { createComment, listComments, deleteComment, updateComment } from "@/api/comment";
import _ from "lodash";

export default Vue.extend({
    name: "RecordComments",
    components: {
        Avatar
    },
    props: {
        recordId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            comments: [],
            newCommentMessage: "",
            editingCommentId: null,
            editedCommentMessage: "",
            commentVisibilityMode: "public"
        };
    },
    computed: {
        ...mapState({
            users: state => state.user.users.data,
            user: state => state.auth.user
        })
    },
    methods: {
        async fetchComments() {
            const response = await listComments("tableRecord", this.recordId)
            this.comments = response.data?.data || [];
            this.$emit('comments-loaded', this.comments);
        },
        getUserName(userId) {
            const user = this.users.find(u => u._id === userId);
            return user ? user.name : "Unknown User";
        },
        getUserAvatar(userId) {
            const user = this.users.find(u => u._id === userId);
            return user ? user.gravatar : null;
        },
        formatDate(date) {
            return moment(date).format("DD MMM YYYY, HH:mm");  
        },
        async addComment() {
            if (!this.newCommentMessage) return;

            const commentData = {
                tableRecord: this.recordId,
                message: this.newCommentMessage,
                user: this.user._id,
                type: "tableRecord",
                visibility: {
                    mode: this.commentVisibilityMode,
                    users: [],
                    departments: []
                }
            };

            await createComment(commentData).then(()=>{
                this.newCommentMessage = "";
                this.fetchComments();
                this.$message.success(this.$t("message.createCommentSuccess"));
            }).catch((error)=>{
                this.$message.error(this.$t("message.createCommentError"));
            })
            
        },
        editComment(comment) {
            if (!this.isCommentOwner(comment)) return;
            this.editingCommentId = comment._id;
            this.editedCommentMessage = comment.message;
        },
        async saveComment(commentId) {
            try {
                await updateComment(commentId, this.editedCommentMessage);
                this.editingCommentId = null;
                this.editedCommentMessage = "";
                this.fetchComments();
                this.$message.success(this.$t("message.updateCommentSuccess"));
            } catch (error) {
                this.$message.error(this.$t("message.updateCommentError"));
            }
        },
        cancelEdit() {
            this.editingCommentId = null;
            this.editedCommentMessage = "";
        },
        async deleteComment(commentId) {
            try {
                await deleteComment(commentId);
                this.fetchComments();
                this.$message.success(this.$t("message.deleteCommentSuccess"));
            } catch (error) {
                this.$message.error(this.$t("message.deleteCommentError"));
            }
        },
        isCommentOwner(comment) {
            return comment.user === this.user._id;
        }
    },
    created() {
        this.fetchComments();
    }
});
</script>

<style scoped lang="scss">
.record-comments {
  padding: 20px;

  .comment-list {
    margin-bottom: 24px;

    .comment-item {
      background-color: #fafafa;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 16px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }

      &.private-comment {
        background-color: #fffbe6;
        border-left: 3px solid #faad14;
      }

      .comment-header {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid #f0f0f0;

        .comment-username {
          margin-left: 12px;
          font-weight: 500;
          color: #262626;
        }

        .private-badge {
          display: flex;
          align-items: center;
          margin-left: 12px;
          padding: 2px 8px;
          background-color: #faad14;
          color: #fff;
          border-radius: 12px;
          font-size: 12px;

          .anticon {
            font-size: 12px;
            margin-right: 4px;
          }
        }

        .comment-date {
          margin-left: auto;
          color: #8c8c8c;
          font-size: 12px;
        }
      }

      .comment-body {
        margin: 12px 0;
        color: #595959;
        
        textarea {
          width: 100%;
          min-height: 80px;
          padding: 8px;
          border: 1px solid #d9d9d9;
          border-radius: 4px;
          margin-bottom: 8px;
          
          &:focus {
            outline: none;
            border-color: #40a9ff;
          }
        }

        button {
          margin-right: 8px;
          padding: 4px 12px;
          border-radius: 4px;
          border: 1px solid #d9d9d9;
          background: #fff;
          cursor: pointer;

          &:hover {
            border-color: #40a9ff;
            color: #40a9ff;
          }
        }
      }

      .comment-actions {
        text-align: right;
        padding-top: 8px;
        border-top: 1px solid #f0f0f0;
        
        a {
          color: #595959;
          margin-left: 16px;
          font-size: 12px;
          cursor: pointer;

          &:hover {
            color: #40a9ff;
          }
        }
      }
    }
  }

  .new-comment {
    background-color: #fff;
    padding: 20px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    textarea {
      width: 100%;
      min-height: 100px;
      padding: 12px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      margin-bottom: 12px;
      resize: vertical;

      &:focus {
        outline: none;
        border-color: #40a9ff;
      }
    }

    .comment-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 12px;

      .visibility-toggle {
        display: flex;
        align-items: center;

        .ant-radio-wrapper {
          display: flex;
          align-items: center;
          margin-right: 16px;

          .anticon {
            margin-right: 4px;
          }
        }
      }

      button {
        padding: 8px 24px;
        background: #1890ff;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          background: #40a9ff;
        }
      }
    }
  }
}
</style>
